import { useEffect } from 'react';
import { trpc } from '~/api/trpc';
import { useBookingFlowStore } from '~/stores/booking-flow-store';

// Helper function to preload an image
const preloadImage = (src: string): Promise<void> => {
    return new Promise((resolve) => {
        const img = new Image();
        img.onload = () => resolve();
        img.onerror = () => resolve(); // Don't fail on error
        img.src = src;
    });
};

// Type for category structure (matches the API response)
type CategoryWithChildren = {
    imageUrl: string | null;
    children: { imageUrl: string | null }[];
};

type ServiceSelectionData = {
    categories?: CategoryWithChildren[];
    symptomCategories?: CategoryWithChildren[];
};

// Collect all image URLs from categories and symptom categories
const collectImageUrls = (data: ServiceSelectionData): string[] => {
    const urls: string[] = [];
    
    // Category images
    data.categories?.forEach((cat) => {
        if (cat.imageUrl) urls.push(cat.imageUrl);
        cat.children?.forEach((child) => {
            if (child.imageUrl) urls.push(child.imageUrl);
        });
    });
    
    // Symptom category images
    data.symptomCategories?.forEach((cat) => {
        if (cat.imageUrl) urls.push(cat.imageUrl);
        cat.children?.forEach((child) => {
            if (child.imageUrl) urls.push(child.imageUrl);
        });
    });
    
    return urls;
};

/**
 * Hook to prefetch all booking funnel CMS data on mount.
 * This ensures zero latency when navigating between booking funnel pages.
 * 
 * All queries are cached for 60 seconds (staleTime: 60000).
 */
export const useBookingFunnelCMSPrefetch = () => {
    const utils = trpc.useUtils();
    const selectedStore = useBookingFlowStore((state) => state.selectedStore);

    useEffect(() => {
        const prefetchQueries = async () => {
            // Batch 1: Critical for first paint (store & path selection screens)
            // These are awaited first so tRPC sends them as a separate, smaller request
            await Promise.all([
                utils.publicBookingFunnel.getStoreSelectionConfig.prefetch(undefined, { trpc: { context: { skipBatch: true } } }),
                utils.publicBookingFunnel.getPathSelectionConfig.prefetch(undefined, { trpc: { context: { skipBatch: true } } }),
            ]);

            // Batch 2: Remaining configs for later funnel steps
            // Fires as a second request after batch 1 completes
            Promise.all([
                utils.publicBookingFunnel.getCalendarSelectionConfig.prefetch(),
                utils.publicBookingFunnel.getEnterDetailsConfig.prefetch(),
                utils.publicBookingFunnel.getBookingReviewConfig.prefetch(),
                utils.publicBookingFunnel.getConfirmationConfig.prefetch(),
            ]);
        };

        prefetchQueries();
    }, [utils]);

    // Prefetch service selection config when store is selected, then preload images
    useEffect(() => {
        if (selectedStore?.id) {
            // Prefetch data first
            utils.publicBookingFunnel.getServiceSelectionConfig.prefetch({ storeId: selectedStore.id }, { trpc: { context: { skipBatch: true } } })
                .then(() => {
                    // Get cached data and preload images
                    const cachedData = utils.publicBookingFunnel.getServiceSelectionConfig.getData({ 
                        storeId: selectedStore.id 
                    });
                    if (cachedData) {
                        const imageUrls = collectImageUrls(cachedData);
                        // Preload all images in parallel (fire and forget)
                        imageUrls.forEach(url => preloadImage(url));
                    }
                });
        }
    }, [selectedStore?.id, utils]);
};
